import { InlineSvg } from 'components/svg';
import { PDFDocumentProxy } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { useState, FC, useMemo, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useBreakpoint } from 'shared';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type Props = {
  url: string;
  onClose: () => void;
};

export const PdfViewWrapper: FC<Props> = ({ url, onClose }) => {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const { isLg, isMd } = useBreakpoint();
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const pageWidth = useMemo(() => {
    if (isLg) return 900;
    if (isMd) return 700;
    return Math.max(containerWidth - 40, 280);
  }, [isLg, isMd, containerWidth]);

  const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy): void => {
    setNumPages(pdf.numPages);
    setIsLoading(false);
  }, []);

  const documentOptions = useMemo(() => ({ cMapUrl: 'cmaps/', standardFontDataUrl: 'standard_fonts/' }), []);

  const goToPrevPage = () => setPageNumber((prevPage) => Math.max(prevPage - 1, 1));
  const goToNextPage = () => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1));

  return (
    <>
      <div className="modal fade show d-block" id="modal_pdf_preview" role="dialog" tabIndex={-1} aria-modal="true">
        <div className="modal-dialog modal-dialog-centered px-md-20" style={{ maxWidth: '100vw', margin: '16px auto' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="fw-bolder">PDF Viewer</h2>
              <div className="d-flex align-items-center">
                <div
                  className="btn btn-icon btn-sm btn-active-icon-primary"
                  onClick={onClose}
                  style={{ cursor: 'pointer' }}
                >
                  <InlineSvg path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
                </div>
              </div>
            </div>
            <div
              ref={containerRef}
              className="modal-body mx-3 d-md-flex justify-content-md-center"
              style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}
            >
              <Document
                key={url}
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div className="text-center">Loading document...</div>}
                options={documentOptions}
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow"
                  loading={<div className="text-center">Loading page...</div>}
                  error={<div className="d-none"></div>}
                  width={pageWidth}
                />
              </Document>
            </div>
            {!isLoading && (
              <div className="modal-footer d-flex justify-content-between align-items-center">
                <button onClick={goToPrevPage} disabled={pageNumber <= 1} className="btn btn-secondary">
                  Previous
                </button>
                <p>
                  Page {pageNumber} of {numPages}
                </p>
                <button onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)} className="btn btn-secondary">
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};
